// Installing the app, as the account knows it (INSTALL.md §4.4, scenario M1).
//
// `GET /api/me/install` answers this view; `POST /api/me/install` takes an
// `InstallReport` and answers it again.
//
// WHY THE ACCOUNT ANSWERS AND NOT THE BROWSER. A browser cannot tell whether
// the app is installed on another device, and on an iPhone the installed app's
// storage is apart from Safari's, so a "Not now" said in one never reaches the
// other. The account holds both, and decides `offerNow` — so a second client
// offers the install on the same terms as this one, not on its own guess.

import { z } from 'zod';

/** A "Not now" holds for this long; then the offer may come back once more. */
export const INSTALL_REOFFER_DAYS = 90;

export const InstallView = z.object({
  /** When an installed copy of the app first reported itself. Null: never. */
  installedAt: z.string().nullable(),
  /** The last "Not now" to the offer. Null: never said. */
  offerClosedAt: z.string().nullable(),
  /**
   * Whether to make the one contextual offer now: never installed anywhere,
   * no "Not now" in the last `INSTALL_REOFFER_DAYS` days, and in a league
   * that has drafted — somebody who has shown they are staying (§4.3). A
   * client still never offers it inside the installed app, which only it can
   * tell.
   */
  offerNow: z.boolean(),
});
export type InstallViewShape = z.infer<typeof InstallView>;

export const InstallReport = z.object({
  /**
   * `installed`: an installed copy of the app is running, sent once per launch
   * and harmless to repeat (the first time is kept). `offer-closed`: "Not now".
   */
  event: z.enum(['installed', 'offer-closed']),
});
export type InstallReportShape = z.infer<typeof InstallReport>;
